import React from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const WebinarCard = ({ webinar, isRegistered, onRegister, onCancelRegistration, onOpenModal, onJoinLive, onWatchRecording }) => {
  const formatDate = (dateString) => {
    if (!dateString) return 'TBD';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = () => {
    switch (webinar?.status) {
      case 'live':
        return (
          <span className="px-3 py-1 bg-error/90 text-white text-xs font-semibold rounded-full flex items-center gap-1.5 shadow-sm">
            <div className="w-2 h-2 bg-white rounded-full animate-pulse" />
            LIVE
          </span>
        );
      case 'upcoming':
        return (
          <span className="px-3 py-1 bg-primary/90 text-white text-xs font-semibold rounded-full shadow-sm">
            Upcoming
          </span>
        );
      case 'completed':
        return (
          <span className="px-3 py-1 bg-success/90 text-white text-xs font-semibold rounded-full shadow-sm">
            Recorded
          </span>
        );
      default:
        return null;
    }
  };

  const isMaxReached = webinar?.max_participants && webinar?.participant_count >= webinar?.max_participants;

  const renderActionButtons = () => {
    const status = webinar?.status;

    if (status === 'live') {
      return (
        <Button
          variant="default"
          size="sm"
          fullWidth
          iconName="Video"
          iconPosition="left"
          onClick={() => onJoinLive ? onJoinLive(webinar) : window.open(webinar?.meeting_url, '_blank')}
          className="bg-error hover:bg-error/90 text-white font-semibold"
        >
          Join Live Now
        </Button>
      );
    }

    if (status === 'upcoming') {
      if (isRegistered) {
        return (
          <div className="flex flex-col gap-2 w-full">
            <div className="flex items-center gap-2 text-xs text-success bg-success/10 px-3 py-2 rounded-lg">
              <Icon name="CheckCircle" size={14} />
              <span>You're registered for this webinar</span>
            </div>
            <Button
              variant="outline"
              size="sm"
              fullWidth
              iconName="X"
              iconPosition="left"
              onClick={() => onCancelRegistration && onCancelRegistration(webinar?.id)}
            >
              Cancel Registration
            </Button>
          </div>
        );
      }

      return (
        <Button
          variant="default"
          size="sm"
          fullWidth
          iconName="UserPlus"
          iconPosition="left"
          onClick={() => onOpenModal && onOpenModal(webinar)}
          disabled={isMaxReached}
          className="font-semibold"
        >
          {isMaxReached ? 'Session Full' : 'Register Now'}
        </Button>
      );
    }

    if (status === 'completed') {
      if (webinar?.recording_url || webinar?.meeting_url) {
        return (
          <Button
            variant="default"
            size="sm"
            fullWidth
            iconName="Play"
            iconPosition="left"
            onClick={() => onWatchRecording ? onWatchRecording(webinar) : window.open(webinar?.recording_url || webinar?.meeting_url, '_blank')}
          >
            Watch Recording
          </Button>
        );
      }
      return (
        <div className="w-full text-center text-xs text-muted-foreground py-2">
          Recording not available yet
        </div>
      );
    }

    return null;
  };

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden hover:shadow-lg transition-all duration-300 group flex flex-col">
      {/* Thumbnail */}
      <div className="relative h-48 overflow-hidden flex-shrink-0">
        <Image
          src={webinar?.thumbnail_url || 'https://images.unsplash.com/photo-1540575467063-178a50c2df87'}
          alt={webinar?.title || 'Webinar thumbnail'}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
        <div className="absolute top-3 right-3">
          {getStatusBadge()}
        </div>
        {webinar?.status === 'live' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 bg-white/90 rounded-full flex items-center justify-center shadow-lg">
              <Icon name="Play" size={28} color="var(--color-error)" />
            </div>
          </div>
        )}
        {/* Category badge */}
        {webinar?.topic && (
          <div className="absolute bottom-3 left-3">
            <span className="px-2.5 py-1 bg-black/60 text-white text-xs font-medium rounded-full backdrop-blur-sm">
              {webinar?.topic}
            </span>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-5 flex flex-col flex-1">
        {/* Title */}
        <h3 className="text-base font-semibold text-foreground mb-2 line-clamp-2 leading-snug">
          {webinar?.title}
        </h3>

        {/* Description */}
        <p className="text-sm text-muted-foreground line-clamp-2 mb-4 flex-1">
          {webinar?.description}
        </p>

        {/* Expert Info */}
        {webinar?.expert && (
          <div className="flex items-center gap-3 mb-4 pb-4 border-b border-border">
            <div className="w-9 h-9 rounded-full overflow-hidden flex-shrink-0 ring-2 ring-border">
              <Image
                src={webinar?.expert?.photo_url || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e'}
                alt={webinar?.expert?.name || 'Expert photo'}
                className="w-full h-full object-cover"
              />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground truncate">
                {webinar?.expert?.name}
              </p>
              <p className="text-xs text-muted-foreground truncate">
                {webinar?.expert?.title}
                {webinar?.expert?.organization && ` · ${webinar?.expert?.organization}`}
              </p>
            </div>
          </div>
        )}

        {/* Meta Info */}
        <div className="grid grid-cols-2 gap-2 mb-4">
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Icon name="Calendar" size={13} />
            <span className="truncate">{formatDate(webinar?.scheduled_at)}</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Icon name="Clock" size={13} />
            <span>{webinar?.duration_minutes || 60} min</span>
          </div>
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Icon name="Users" size={13} />
            <span>{webinar?.participant_count || 0} registered</span>
          </div>
          {webinar?.language && (
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Icon name="Globe" size={13} />
              <span>{webinar?.language}</span>
            </div>
          )}
        </div>

        {/* Capacity warning */}
        {isMaxReached && webinar?.status === 'upcoming' && !isRegistered && (
          <div className="flex items-center gap-2 text-xs text-warning bg-warning/10 px-3 py-2 rounded-lg mb-3">
            <Icon name="AlertCircle" size={14} />
            <span>Session is full</span>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2 mt-auto pt-2">
          {renderActionButtons()}
        </div>
      </div>
    </div>
  );
};

export default WebinarCard;